import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Components, ThisGorillaUtils } from "@thisgorilla/primate-ds";
import styled from "styled-components";
import { API_CALLS } from "../../../../../../API/api_calls";
import { actions, useAppDispatch } from "../../../../../../redux";
import { ArticleProps } from "../../../../../../redux/slices/article/Article";
import { FlexRowCenter, FlexRowSpaced } from "../../../../../Common/Styles";
import { useFindContent } from "../../../useFindContent";
import ImageProps from "../Image";
import ImageItem from "./ImageItem";

const ImageGallery = ({
  fileInputRef,
  setIsPreviewOpen,
  id,
  api_status,
  uploadedImages,
}: ImageProps.ImageGallery) => {
  const dispatch = useAppDispatch();
  const { content_data } = useFindContent(id) as ArticleProps.WithImageContent;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = content_data.findIndex(
        (content) => content.id === String(active.id)
      );
      const newIndex = content_data.findIndex(
        (content) => content.id === String(over?.id)
      );
      const newBodyContent = arrayMove(content_data, oldIndex, newIndex);
      API_CALLS.ARTICLE.reorderImagesInAnImageBlock(
        id,
        newBodyContent.map((image) => image.id)
      );
      dispatch(
        actions.article.setImageGalleryOrder({ imageList: newBodyContent, id })
      );
    }
  };
  return (
    <MultiImagePreviewContainer>
      <Container>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={content_data || []}
            strategy={rectSortingStrategy}
            disabled={api_status === "progress"}
          >
            {(content_data || []).map((image, index) => (
              <ImageItem id={image.id} key={index} order_id={id} />
            ))}
          </SortableContext>
        </DndContext>
        {api_status === "progress" &&
          Array.from({ length: uploadedImages }, (_, i) => (
            <ImageItem id={String(i)} key={i} order_id={i} onlyForLoader />
          ))}
        <AddImage $disabled={false}>
          <Components.IconButton
            iconType="Plus"
            onClick={() => fileInputRef?.current?.click()}
            disabled={false}
            kind="Secondary"
            size="Large"
          />
        </AddImage>
      </Container>
      <Components.Button
        color="Primary"
        size="Medium"
        label="Save"
        kind="Primary"
        disabled={api_status === "progress"}
        onClick={() => {
          setIsPreviewOpen(true);
        }}
      />
    </MultiImagePreviewContainer>
  );
};

export default ImageGallery;

const MultiImagePreviewContainer = styled(FlexRowSpaced)`
  width: 100%;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-m"].value}rem;
  box-shadow: none;
  border: none;
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-secondary"].value};

  &:hover {
    background: ${({ theme }) =>
      theme.Color.elevation.surface["surface-primary-hover"].value};
  }
`;

const Container = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  flex-wrap: wrap;
`;

const AddImage = styled(FlexRowCenter)<{ $disabled: boolean }>`
  min-width: ${ThisGorillaUtils.convertPxToRem("70")}rem;
  height: ${ThisGorillaUtils.convertPxToRem("70")}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xs"].value}rem;
  background: ${({ theme, $disabled }) =>
    $disabled
      ? theme.Color.background.system["background-disabled"].value
      : theme.Color.background.brand.subtlest["default-hover"].value};
  cursor: pointer;
`;
